import { MapContainer, TileLayer, CircleMarker, Popup, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { useEffect, useMemo, useRef, useState } from 'react';
import { formatIndianStyle } from '../helper/arrayFormatter';

const AUTO_CENTER_MIN_ZOOM = 8;

const HoverAutoCenter = ({ airport, minZoom = AUTO_CENTER_MIN_ZOOM }) => {
	const map = useMap();
	const lastCenteredAirportCode = useRef('');

	useEffect(() => {
		if (!airport?.code) {
			lastCenteredAirportCode.current = '';
			return;
		}

		const latitude = Number(airport.lat);
		const longitude = Number(airport.lng);
		if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
			return;
		}

		if (map.getZoom() < minZoom || lastCenteredAirportCode.current === airport.code) {
			return;
		}

		map.setView([latitude, longitude], map.getZoom(), { animate: true });
		lastCenteredAirportCode.current = airport.code;
	}, [airport, map, minZoom]);

	return null;
};

const MapRender = ({ data, isAdminPage = false, AirportsLoc = [], activeAirportCode = '' }) => {
	const [UpdatedData, setUpdatedData] = useState([]);
	const [hoveredAirportCode, setHoveredAirportCode] = useState('');
	const markerRefs = useRef({});
	const previousActiveAirportCode = useRef('');

	useEffect(() => {
		const tempAPLoc = AirportsLoc?.map(item => {
			return {
				code: item?.[0],
				name: item?.[1],
				lat: item?.[5],
				lng: item?.[6]
			}
		})

		// Create a dictionary of airport names and their respective traffic values from the 'data' prop
		let tempDict = {};
		data?.forEach((item) => {
			tempDict[item?.[0]] = item?.[1]; // item[0] is the airport name, item[1] is the traffic value
		});

		// Find the largest traffic value in the data
		let maxTraffic = 0;
		let tempData = tempAPLoc.filter((item) => isAdminPage || tempDict[item.code]).map((item) => {
			const traffic = tempDict[item.code] || 0;
			// Find the maximum traffic value
			maxTraffic = Math.max(maxTraffic, traffic);
			return {
				...item,
				traffic, // Add traffic to the item
			};
		});

		// Now set the radius based on the largest traffic value
		tempData = tempData.map((item) => {
			let radius = 0; // Default minimum radius

			if (item.traffic > 0) {
				// Scale radius between 5 and 30
				radius = 3 + ((item.traffic / maxTraffic) * 27); // This will scale the radius from 5 to 30
			}

			return {
				...item,
				radius, // Add the radius field to the item
			};
		});

		// Set the updated data with the radius field
		setUpdatedData(tempData);
	}, [data, AirportsLoc, isAdminPage]);

	useEffect(() => {
		const previousCode = previousActiveAirportCode.current;

		if (previousCode && previousCode !== activeAirportCode && previousCode !== hoveredAirportCode) {
			markerRefs.current[previousCode]?.closePopup?.();
		}

		if (activeAirportCode) {
			markerRefs.current[activeAirportCode]?.openPopup?.();
		}

		previousActiveAirportCode.current = activeAirportCode || '';
	}, [UpdatedData, activeAirportCode, hoveredAirportCode]);

	const activeAirport = useMemo(
		() => UpdatedData.find((airport) => airport?.code === activeAirportCode),
		[UpdatedData, activeAirportCode]
	);

	// Size of the map
	const mapCenter = [20.5937, 78.9629]; // Center of India
	const zoomLevel = 4;

	return (
		<div style={{ height: '500px', width: '100%', overflow: "hidden", borderRadius: 10, marginTop: isAdminPage ? 5 : 65, boxShadow: '0 4px 10px rgba(0, 0, 0, 0.1)' }}>
			<MapContainer center={mapCenter} zoom={zoomLevel} style={{ width: '100%', height: '550px' }}>
				<TileLayer url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png" />
				<HoverAutoCenter airport={activeAirport} />

				{UpdatedData?.map((ap, index) => {
					const isActive = ap?.code === activeAirportCode || ap?.code === hoveredAirportCode;
					const baseRadius = isAdminPage ? 2 : ap?.radius;
					const activeRadius = isAdminPage ? 4 : (ap?.radius > 0 ? ap?.radius + 2 : 5);
					const markerColor = isActive ? 'orange' : '#1890ff';

					return <CircleMarker
						key={index}
						ref={(marker) => {
							if (marker) {
								markerRefs.current[ap?.code] = marker;
								return;
							}

							delete markerRefs.current[ap?.code];
						}}
						center={[ap?.lat, ap?.lng]}
						radius={isActive ? activeRadius : baseRadius}
						pathOptions={{
							color: markerColor,
							weight: 1.5,
							fillColor: markerColor,
							fillOpacity: isAdminPage ? 1 : isActive ? isAdminPage ? 1 : 0.3 : 0.3,
						}}
						eventHandlers={{
							mouseover: (e) => {
								setHoveredAirportCode(ap?.code || '');
								e.target.openPopup();
							},
							mouseout: (e) => {
								setHoveredAirportCode((current) => (current === ap?.code ? '' : current));
								if (activeAirportCode !== ap?.code) {
									e.target.closePopup();
								}
							},
						}}
					>
						<Popup autoPan={true}>
							<strong className=''>{ap?.code}</strong><br />
							<p style={{ marginTop: "5px" }}>{isAdminPage ? ap?.name : formatIndianStyle(ap?.traffic)}</p>
						</Popup>
					</CircleMarker>
				})}
			</MapContainer>
		</div>
	);
};

export default MapRender;
